"use client";
import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import Skeleton from "../../feedback/Skeleton/Skeleton.js";
import { usePolymorphic } from "../../../hooks/usePolymorphic.js";
import { useTracking } from "../../../hooks/useTracking.js";
import Paragraph from "../../typography/Paragraph/Paragraph.js";
import { log } from "../../../utilities/index.js";
import { rem, Box } from "@mantine/core";
import { useDidUpdate } from "@mantine/hooks";
import { forwardRef, useState, useEffect } from "react";
import { resolveLocalAsset } from "./Image.helpers.js";
import PlaceholderImage, { I as ImageStyles } from "./PlaceholderImage.js";
const _FigureImage = forwardRef((props, ref) => {
  const {
    loaded,
    width,
    height,
    src,
    alt,
    title,
    caption,
    byline,
    captionAlign = "center",
    polymorphicProps,
    disablePolymorphic,
    fit,
    shadow,
    objectPosition,
    containerWidth,
    className,
    style,
    placeholder,
    overlay,
    sizes,
    overlayOpacity = 1,
    ...rest
  } = props;
  const w = width === "parent" || width === "edge" ? "100%" : rem(width);
  const h = height === "parent" ? "100%" : rem(height);
  const fill = width === "100%" || width === "edge" || (polymorphicProps == null ? void 0 : polymorphicProps.fill);
  const styleVariables = {
    "--passed-width-type": width,
    "--passed-height-type": height,
    "--passed-calc-width": w,
    "--passed-calc-height": h,
    "--passed-fit": fit,
    "--passed-object-position": objectPosition,
    "--passed-overlay": overlay,
    "--passed-overlay-opacity": overlayOpacity
  };
  const { image: PolymorphicImage } = usePolymorphic();
  const [error, setError] = useState(!src);
  const tracker = useTracking();
  useDidUpdate(() => {
    if (!src) {
      log({ type: "error", message: "The source was not found", tracker });
    }
    setError(!src);
  }, [src]);
  useEffect(() => {
    if (!alt && !error) {
      log({ type: "warning", message: "Image component is missing alt text", tracker });
    }
  }, [alt]);
  return /* @__PURE__ */ jsxs(
    Box,
    {
      ...rest,
      pos: width !== "edge" ? "relative" : void 0,
      className: `${ImageStyles.root} ${className}`,
      ref,
      style: { ...styleVariables, ...style },
      children: [
        !loaded && /* @__PURE__ */ jsx(Skeleton, { width: "100%", height: "100%" }),
        loaded && /* @__PURE__ */ jsxs(Fragment, { children: [
          width === "edge" && /* @__PURE__ */ jsx("div", { className: ImageStyles.heightPlaceholder, "data-width-type": width }),
          /* @__PURE__ */ jsxs("figure", { className: ImageStyles.figure, "data-width-type": width, children: [
            /* @__PURE__ */ jsx("div", { className: ImageStyles.imageWrapper, "data-shadow": shadow, children: error ? /* @__PURE__ */ jsx(
              PlaceholderImage,
              {
                width: w,
                height: h,
                tracker,
                placeholder,
                title
              }
            ) : PolymorphicImage && !disablePolymorphic ? /* @__PURE__ */ jsx(
              PolymorphicImage,
              {
                ...polymorphicProps,
                fill,
                width: !fill ? width : void 0,
                height: !fill ? height : void 0,
                src,
                fit,
                className: ImageStyles.image,
                "data-width-type": width,
                "data-height-type": height,
                alt,
                title,
                sizes: sizes || rem(containerWidth) || "100vw",
                onError: () => {
                  log({
                    type: "error",
                    message: "An error happened rendering this image",
                    tracker
                  });
                  setError(true);
                },
                style: { inset: "revert-layer" }
              }
            ) : /* @__PURE__ */ jsx(
              "img",
              {
                width: w,
                height: h,
                src: resolveLocalAsset(src, tracker),
                alt,
                title,
                "data-width-type": width,
                "data-height-type": height,
                sizes: sizes || "100vw",
                className: ImageStyles.image,
                onError: () => {
                  log({
                    type: "error",
                    message: "An error happened rendering this image",
                    tracker
                  });
                  setError(true);
                }
              }
            ) }),
            caption && /* @__PURE__ */ jsx(Paragraph, { size: "caption", align: captionAlign, text: caption, mt: "xs" }),
            byline && /* @__PURE__ */ jsx(
              Paragraph,
              {
                size: "caption",
                c: "var(--figurine-body-neutral)",
                align: captionAlign,
                text: byline
              }
            )
          ] }),
          overlay && width !== "edge" && /* @__PURE__ */ jsx(Box, { className: ImageStyles.overlay })
        ] })
      ]
    }
  );
});
const FigureImage = _FigureImage;
_FigureImage.displayName = "FigureImage";
export {
  FigureImage as default
};
